import { FileText, CalendarDays, Package, Truck, CreditCard, Phone, Leaf, ShieldCheck, Heart, Smile } from 'lucide-react'
import { SELLER, calc, inWords, r2, code128, QrMock, DMatrixMock } from './invoiceShared'

/* ═══════════════════════════════════════════════════════════
   TEMPLATE 2 — SUPADAA TAX INVOICE
   File: SupadaaInvoiceSheet.jsx  (Invoice.jsx ni bajuma j rakhvi)
   Single page, light "paper" design (print / PDF ma pan same)
═══════════════════════════════════════════════════════════ */

/* je shipping partner invoice ma batavvo hoy te */
const SHIPPING_PARTNER = 'Amazon Shipping'

const money = n => '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const LeafMark = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M21 2C10 2 3.5 7.5 3.5 15c0 2 .8 4 2.6 5.3C6.3 14.5 10 9.8 17 7.5c-5.6 3-8.6 7.2-9.4 12.6C17.6 21 21 13 21 2z" />
  </svg>
)

const Logo = ({ small }) => (
  <span className={small ? 'sd-logo sd-logo-s' : 'sd-logo'}>
    <LeafMark className="sd-logo-leaf" />
    Supadaa<sup>®</sup>
  </span>
)

function Barcode({ text }) {
  const { bars, total } = code128(text)
  return (
    <svg className="sd-bars" viewBox={`0 0 ${total} 40`} preserveAspectRatio="none" fill="currentColor" aria-hidden="true">
      {bars.map(([x, w], i) => <rect key={i} x={x} y="0" width={w} height="40" />)}
    </svg>
  )
}

const PERKS = [
  { Icon: Leaf, a: 'Eco Friendly', b: 'Products' },
  { Icon: ShieldCheck, a: 'Quality', b: 'Checked' },
  { Icon: Heart, a: 'Packed', b: 'with Care' },
  { Icon: Smile, a: 'Happy', b: 'To Serve' },
]

export function SupadaaInvoiceSheet({ inv }) {
  const c = calc(inv)

  const gross = r2(c.rows.reduce((a, r) => a + Number(r.unitPrice) * Number(r.qty), 0))
  const disc = r2(c.rows.reduce((a, r) => a + Number(r.discount || 0) * Number(r.qty), 0))
  const ship = Number(inv.shippingCharge || 0) // invoice ma shipping charge hoy to j add thase
  const grand = r2(c.total + ship)
  const rates = [...new Set(c.rows.map(r => r.taxRate))]
  const gstLabel = rates.length === 1 ? `GST (${rates[0]}%)` : 'GST'

  const lines = inv.billing?.lines || []
  const shipLines = inv.shipping?.lines || lines
  const phone = inv.phone || inv.billing?.phone || inv.raw?.phone || ''
  const mode = inv.payment?.mode || 'COD'
  const isCod = /cod/i.test(mode)
  const track = inv.label?.awb || inv.id

  const AddrCard = ({ title, ls }) => (
    <div className="sd-card">
      <h3>{title}</h3>
      <div className="sd-card-b">
        <div className="sd-name">{ls[0]}</div>
        {ls.slice(1).map((l, i) => <div key={i}>{l}</div>)}
        {phone && <div className="sd-phone"><Phone size={14} /> {phone}</div>}
      </div>
    </div>
  )

  return (
    <div className="sd-sheet">
      <div className="sd-body">
        {/* Header */}
        <div className="sd-head">
          <div className="sd-brand">
            <Logo />
            <div className="sd-tag">Thoughtful Products. A Better Tomorrow.</div>
          </div>
          <div className="sd-vline" />
          <div className="sd-title">
            <h1>TAX INVOICE</h1>
            <p>Original for Recipient</p>
            <p>(Not for Resale)</p>
          </div>
        </div>

        {/* Invoice meta */}
        <div className="sd-meta">
          <div className="sd-meta-i"><FileText size={28} strokeWidth={1.6} /><div><span>Invoice No.</span><b>{inv.id}</b></div></div>
          <div className="sd-meta-i"><CalendarDays size={28} strokeWidth={1.6} /><div><span>Invoice Date</span><b>{inv.date}</b></div></div>
          <div className="sd-meta-i"><Package size={28} strokeWidth={1.6} /><div><span>Order ID</span><b>#{inv.orderNo}</b></div></div>
        </div>

        {/* Bill / Ship */}
        <div className="sd-addr">
          <AddrCard title="Bill To" ls={lines} />
          <AddrCard title="Ship To" ls={shipLines} />
        </div>

        {/* Shipping strip */}
        <div className="sd-ship">
          <div className="sd-ship-i"><Truck size={20} /><div><span>Shipping Partner</span><em>{SHIPPING_PARTNER}</em></div></div>
          <div className="sd-ship-i"><CreditCard size={20} /><div><span>Payment Mode</span><em>{mode}</em>{isCod && <small>(Cash on Delivery)</small>}</div></div>
          <div className="sd-ship-i"><Package size={20} /><div><span>Courier Tracking No.</span><em>{track}</em></div></div>
          <div className="sd-ship-i"><CalendarDays size={20} /><div><span>Order Placed On</span><em>{inv.orderDate}</em></div></div>
        </div>

        {/* Items */}
        <div className="sd-tw">
          <table className="sd-table">
            <thead>
              <tr>
                <th className="c">#</th><th>Product Description</th><th className="c">HSN/SAC</th>
                <th className="c">Qty</th><th className="c">Unit Price<br />(₹)</th><th className="c">Total Price<br />(₹)</th>
              </tr>
            </thead>
            <tbody>
              {c.rows.map((r, i) => (
                <tr key={i}>
                  <td className="c">{i + 1}</td>
                  <td className="sd-pd">{r.desc}</td>
                  <td className="c">{r.hsn}</td>
                  <td className="c">{r.qty}</td>
                  <td className="c">{money(r.unitPrice)}</td>
                  <td className="c">{money(Number(r.unitPrice) * Number(r.qty))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Words + totals */}
        <div className="sd-sum">
          <div className="sd-sum-l">
            <div className="sd-words">
              <b>Amount in Words</b>
              <div>Rupees {inWords(grand).replace(/ only$/, ' Only')}</div>
            </div>
            <div className="sd-gst">
              <FileText size={26} strokeWidth={1.5} />
              <div>
                <b>GST Details</b>
                <dl>
                  <dt>GSTIN</dt><dd>: {SELLER.gst}</dd>
                  <dt>State</dt><dd>: Gujarat ({SELLER.stateCode})</dd>
                  <dt>Place of Supply</dt><dd>: {inv.supply}{inv.shipping?.code ? ` (${inv.shipping.code})` : ''}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="sd-tot">
            <div><span>Subtotal</span><span>{money(gross)}</span></div>
            <div><span>Discount</span><span>{money(disc)}</span></div>
            <div><span>{gstLabel}</span><span>{money(c.tax)}</span></div>
            <div><span>Shipping Charges</span><span>{money(ship)}</span></div>
            <div className="sd-grand"><span>Total Amount</span><span>{money(grand)}</span></div>
          </div>
        </div>

        {/* Barcode / QR / Thank you */}
        <div className="sd-strip">
          <div className="sd-st-c">
            <Barcode text={track} />
            <div className="sd-bc-t">{track}</div>
          </div>
          <div className="sd-st-c sd-st-qr">
            <QrMock seed={inv.id} />
            <small>Scan for Order Tracking</small>
          </div>
          <div className="sd-st-c sd-thanks">
            <div className="sd-script">Thank You</div>
            <small>for choosing Supadaa</small>
            <Heart size={16} fill="currentColor" />
          </div>
          <div className="sd-st-c sd-st-logo">
            <Logo small />
            <small>supadaa.com</small>
          </div>
        </div>

        {/* Perks */}
        <div className="sd-perks">
          {PERKS.map(({ Icon, a, b }) => (
            <div key={a}><Icon size={30} strokeWidth={1.6} /><span>{a}<br />{b}</span></div>
          ))}
        </div>
      </div>

      <div className="sd-bar">Thank you for being a part of our journey <Heart size={11} fill="currentColor" /></div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   TEMPLATE 2 — PAGE 1 : SHIPPING LABEL (Supadaa design)
   Data Template 1 na label jevo j (inv.label)
═══════════════════════════════════════════════════════════ */
export function SupadaaLabelSheet({ inv }) {
  const L = inv.label
  if (!L) return null
  const sort = L.sort || L.station
  const shipDate = L.shipDate || '28/07/2026'
  const [dd, mm, yy] = shipDate.split('/')
  const to = L.to || []
  const rsh = L.code === 'RSH'

  return (
    <div className="sd-sheet sd-label">
      <div className="sd-body">
        {/* Header */}
        <div className="sd-head">
          <div className="sd-brand">
            <Logo />
            <div className="sd-tag">Thoughtful Products. A Better Tomorrow.</div>
          </div>
          <div className="sd-vline" />
          <div className="sd-title">
            <h1>SHIPPING LABEL</h1>
            <p>Original for Courier Partner</p>
            <p>(Handle with Care)</p>
          </div>
        </div>

        {/* AWB barcode + tags */}
        <div className="sd-awb">
          <div className="sd-awb-code">
            <Barcode text={L.awb} />
            <div className="sd-bc-t">AWB {L.awb}</div>
          </div>
          <div className="sd-tags">
            <div className={`sd-tg big${rsh ? ' solid' : ''}`}><b>{L.code}</b></div>
            <div className="sd-tg">{L.flag?.length > 0 ? <b>{L.flag[0]}<br />{L.flag[1]}</b> : <span>—</span>}</div>
            <div className="sd-tg"><span>Weight</span><b>{L.weight} kgs</b></div>
            <div className="sd-tg"><span>Amount Due</span><b>{L.due}</b></div>
            <div className="sd-tg"><span>Payment</span><b>{L.pay}</b></div>
            <div className="sd-tg"><span>Package</span><b>{L.box || 'BOX 1 of 1'}</b></div>
          </div>
        </div>

        {/* Order meta */}
        <div className="sd-meta">
          <div className="sd-meta-i"><Package size={28} strokeWidth={1.6} /><div><span>Order ID</span><b>#{inv.orderNo}</b></div></div>
          <div className="sd-meta-i"><CalendarDays size={28} strokeWidth={1.6} /><div><span>Ship Date</span><b>{shipDate}</b></div></div>
          <div className="sd-meta-i"><FileText size={28} strokeWidth={1.6} /><div><span>Invoice No.</span><b>{inv.id}</b></div></div>
        </div>

        {/* Ship To / Ship From */}
        <div className="sd-addr">
          <div className="sd-card">
            <h3>Ship To</h3>
            <div className="sd-card-b">
              <div className="sd-name">{to[0]}</div>
              {to.slice(1).map((l, i) => <div key={i}>{l}</div>)}
            </div>
          </div>
          <div className="sd-card">
            <h3>Ship From</h3>
            <div className="sd-card-b">
              <div className="sd-name">{SELLER.name}</div>
              <div>Return Address:</div>
              {SELLER.lines.map(l => <div key={l}>{l}</div>)}
            </div>
          </div>
        </div>

        {/* Station / Sector / Sortzone */}
        <div className="sd-sort">
          <div><span>DELIVERY STATION</span><b>{L.station}</b></div>
          <div className="solid"><span>SECTOR</span><b>{L.sector}</b></div>
          <div><span>SORTZONE</span><b>{sort}</b></div>
        </div>

        {/* Data matrix codes */}
        <div className="sd-dm">
          {[0, 1, 2, 3].map(i => <DMatrixMock key={i} seed={inv.id + i} />)}
        </div>

        {/* Declaration */}
        <div className="sd-decl">Customer Self Declaration : The goods sold are intended for end user consumption. Not for resale.</div>
        <div className="sd-tw">
          <table className="sd-table sd-mini">
            <thead>
              <tr><th className="c">#</th><th>Seller</th><th>GSTIN</th><th>Invoice</th><th>Date</th><th>Item Type</th></tr>
            </thead>
            <tbody>
              <tr>
                <td className="c">1</td><td>{SELLER.name}</td><td>{SELLER.gst}</td><td>{inv.id}</td><td>{`${mm}-${dd}-${yy}`}</td><td>{L.itemType}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Route */}
        {L.route?.length > 0 && (
          <div className="sd-route">
            {L.route.map((c, i) => (
              <div key={i}>
                <div className="sd-rt-top">{c.top}</div>
                {c.chip && <div className="sd-rt-chip"><i>{c.chip}</i><b>{c.code}</b></div>}
              </div>
            ))}
          </div>
        )}

        <div className="sd-lfoot"><span>Sold on: www.amazon.in</span><b>ATSPL</b></div>
      </div>

      <div className="sd-bar">Thank you for being a part of our journey <Heart size={11} fill="currentColor" /></div>
    </div>
  )
}

/* ─────────────────────────  CSS (scoped to .sd-sheet)  ───────────────────────── */
export const SD_CSS = `
.sd-sheet, .sd-sheet * { box-sizing: border-box; }
.sd-sheet {
  --sd-g: #1d4a2c; --sd-bg: #faf7f0; --sd-panel: #e8eee6; --sd-line: #cfd9cb; --sd-ink: #1b2a20; --sd-soft: #5d6e62;
  font-family: 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 12px; line-height: 1.4; text-align: left;
  color: var(--sd-ink); background: var(--sd-bg);
  display: flex; flex-direction: column; aspect-ratio: 210 / 297;
  border-radius: 10px; border: 1px solid #d9d9d9; overflow: hidden;
}
.sd-sheet svg { flex-shrink: 0; }
.sd-body { flex: 1; display: flex; flex-direction: column; justify-content: space-between; gap: 10px; padding: 20px 24px 12px; }

.sd-head { display: grid; grid-template-columns: 1.15fr auto 1fr; align-items: center; gap: 22px; padding-bottom: 2px; }
.sd-brand { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.sd-logo { position: relative; display: inline-block; align-self: flex-start; font-size: 54px; font-weight: 800; letter-spacing: -2.5px; line-height: 1; color: var(--sd-g); padding-top: 18px; }
.sd-logo sup { font-size: 12px; font-weight: 600; letter-spacing: 0; vertical-align: top; position: relative; top: 8px; margin-left: 2px; }
.sd-logo-leaf { position: absolute; top: 0; left: 55%; width: 28px; height: 28px; }
.sd-logo-s { font-size: 30px; letter-spacing: -1.4px; padding-top: 11px; }
.sd-logo-s sup { font-size: 8px; top: 4px; }
.sd-logo-s .sd-logo-leaf { width: 16px; height: 16px; }
.sd-tag { font-size: 12.5px; color: var(--sd-g); }
.sd-vline { width: 2px; height: 74px; background: var(--sd-g); }
.sd-title { text-align: right; }
.sd-title h1 { margin: 0 0 6px; font-size: 34px; font-weight: 800; letter-spacing: .5px; color: var(--sd-g); line-height: 1.05; }
.sd-title p { margin: 0; font-size: 14px; color: var(--sd-soft); line-height: 1.5; }

.sd-meta { display: grid; grid-template-columns: repeat(3, 1fr); background: var(--sd-panel); border-radius: 10px; padding: 12px 4px; }
.sd-meta-i { display: flex; align-items: center; gap: 12px; padding: 0 16px; border-right: 1px solid var(--sd-line); color: var(--sd-g); min-width: 0; }
.sd-meta-i:last-child { border-right: none; }
.sd-meta-i div { display: flex; flex-direction: column; min-width: 0; color: var(--sd-ink); }
.sd-meta-i span { font-size: 12px; font-weight: 600; }
.sd-meta-i b { font-size: 14.5px; font-weight: 500; overflow-wrap: anywhere; }

.sd-addr { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.sd-card { border: 1px solid var(--sd-line); border-radius: 10px; overflow: hidden; background: #fff; min-width: 0; }
.sd-card h3 { margin: 0; padding: 7px 14px; font-size: 15px; font-weight: 700; color: var(--sd-g); background: var(--sd-panel); }
.sd-card-b { padding: 10px 14px 12px; font-size: 13px; line-height: 1.65; overflow-wrap: anywhere; }
.sd-name { font-size: 15.5px; font-weight: 700; }
.sd-phone { display: flex; align-items: center; gap: 10px; margin-top: 6px; color: var(--sd-g); }
.sd-phone { color: var(--sd-ink); }
.sd-phone svg { color: var(--sd-g); }

.sd-ship { display: grid; grid-template-columns: repeat(4, 1fr); border: 1px solid var(--sd-line); border-radius: 10px; padding: 10px 2px; background: #fff; }
.sd-ship-i { display: flex; align-items: flex-start; gap: 10px; padding: 0 12px; border-right: 1px solid var(--sd-line); color: var(--sd-g); min-width: 0; }
.sd-ship-i:last-child { border-right: none; }
.sd-ship-i div { display: flex; flex-direction: column; gap: 3px; min-width: 0; color: var(--sd-ink); }
.sd-ship-i span { font-size: 11.5px; font-weight: 700; }
.sd-ship-i em { font-style: normal; font-size: 12px; color: var(--sd-ink); overflow-wrap: anywhere; }
.sd-ship-i small { font-size: 10px; color: var(--sd-soft); }

.sd-tw { border: 1px solid var(--sd-g); border-radius: 10px; overflow-x: auto; background: #fff; }
.sd-table { width: 100%; min-width: 560px; border-collapse: collapse; }
.sd-table th { background: var(--sd-g); color: #fff; font-size: 13px; font-weight: 700; padding: 9px 10px; text-align: left; line-height: 1.25; border-left: 1px solid rgba(255,255,255,.25); }
.sd-table th:first-child { border-left: none; }
.sd-table td { padding: 12px 10px; font-size: 13px; border-left: 1px solid var(--sd-line); }
.sd-table td:first-child { border-left: none; }
.sd-table .c { text-align: center; }
.sd-table .sd-pd { font-size: 13.5px; }

.sd-sum { display: grid; grid-template-columns: 1.1fr 1fr; gap: 22px; align-items: start; }
.sd-sum-l { display: flex; flex-direction: column; gap: 12px; padding-top: 14px; min-width: 0; }
.sd-words { padding-bottom: 12px; border-bottom: 1px solid var(--sd-line); }
.sd-words b { display: block; font-size: 12.5px; color: var(--sd-g); margin-bottom: 4px; }
.sd-words div { font-size: 13.5px; }
.sd-gst { display: flex; gap: 12px; color: var(--sd-g); }
.sd-gst b { font-size: 13.5px; color: var(--sd-g); }
.sd-gst dl { display: grid; grid-template-columns: auto 1fr; column-gap: 12px; margin: 4px 0 0; font-size: 11.5px; color: var(--sd-ink); }
.sd-gst dt { font-weight: 400; } .sd-gst dd { margin: 0; overflow-wrap: anywhere; }

.sd-tot { border: 1px solid var(--sd-line); border-radius: 10px; overflow: hidden; background: #fff; }
.sd-tot > div { display: grid; grid-template-columns: 1fr 1fr; font-size: 13.5px; }
.sd-tot > div + div { border-top: 1px solid var(--sd-line); }
.sd-tot > div > span { padding: 9px 14px; }
.sd-tot > div > span + span { text-align: right; border-left: 1px solid var(--sd-line); padding-right: 22px; }
.sd-tot .sd-grand { background: var(--sd-panel); color: var(--sd-g); font-weight: 700; }
.sd-tot .sd-grand > span { padding-top: 13px; padding-bottom: 13px; font-size: 16px; }
.sd-tot .sd-grand > span + span { font-size: 21px; border-left: none; }

.sd-strip { display: grid; grid-template-columns: 1.15fr .8fr 1.1fr 1fr; align-items: center; background: var(--sd-panel); border-radius: 10px; padding: 12px 0; }
.sd-st-c { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; padding: 0 16px; border-right: 1px solid var(--sd-line); min-height: 88px; min-width: 0; }
.sd-st-c:last-child { border-right: none; }
.sd-bars { width: 100%; height: 52px; color: #111; }
.sd-bc-t { font-size: 13.5px; letter-spacing: .5px; overflow-wrap: anywhere; text-align: center; }
.sd-sheet .ai-qr { width: 66px; height: 66px; margin: 0; color: #111; background: #fff; padding: 2px; }
.sd-st-c small { font-size: 9.5px; color: var(--sd-soft); text-align: center; }
.sd-thanks { color: var(--sd-g); }
.sd-script { font-family: 'Segoe Script', 'Brush Script MT', 'Lucida Handwriting', cursive; font-style: italic; font-size: 27px; line-height: 1.1; }
.sd-thanks small { font-size: 12px; color: var(--sd-ink); }
.sd-st-logo small { font-size: 13px; color: var(--sd-ink); }

.sd-perks { display: grid; grid-template-columns: repeat(4, 1fr); padding: 2px 0 0; }
.sd-perks > div { display: flex; flex-direction: column; align-items: center; gap: 6px; text-align: center; color: var(--sd-g); border-right: 1px solid var(--sd-line); padding: 0 8px; }
.sd-perks > div:last-child { border-right: none; }
.sd-perks span { font-size: 13px; color: var(--sd-ink); line-height: 1.35; }

.sd-bar { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 14px 20px; background: var(--sd-g); color: #fff; font-size: 10px; letter-spacing: .32em; text-transform: uppercase; }

/* ── Template 2 : shipping label ── */
.sd-awb { display: grid; grid-template-columns: 1.2fr 1fr; gap: 14px; align-items: stretch; }
.sd-awb-code { display: flex; flex-direction: column; justify-content: center; gap: 8px; background: #fff; border: 1px solid var(--sd-line); border-radius: 10px; padding: 14px 16px; min-width: 0; }
.sd-awb-code .sd-bars { height: 84px; }
.sd-awb-code .sd-bc-t { font-size: 15px; letter-spacing: 1px; }
.sd-tags { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.sd-tg { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; min-height: 54px; padding: 6px 4px; text-align: center; background: #fff; border: 1px solid var(--sd-line); border-radius: 10px; overflow-wrap: anywhere; min-width: 0; }
.sd-tg span { font-size: 10px; font-weight: 600; color: var(--sd-soft); }
.sd-tg b { font-size: 15px; font-weight: 800; color: var(--sd-g); line-height: 1.15; }
.sd-tg.big b { font-size: 22px; }
.sd-tg.solid { background: var(--sd-g); border-color: var(--sd-g); }
.sd-tg.solid b { color: #fff; }

.sd-sort { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.sd-sort > div { display: flex; flex-direction: column; gap: 2px; padding: 8px 14px; background: #fff; border: 1px solid var(--sd-line); border-radius: 10px; min-width: 0; }
.sd-sort span { font-size: 10.5px; font-weight: 700; letter-spacing: .08em; color: var(--sd-soft); }
.sd-sort b { font-size: 30px; font-weight: 800; line-height: 1.1; color: var(--sd-g); overflow-wrap: anywhere; }
.sd-sort .solid { background: var(--sd-g); border-color: var(--sd-g); }
.sd-sort .solid span { color: rgba(255, 255, 255, .78); }
.sd-sort .solid b { color: #fff; }

.sd-dm { display: grid; grid-template-columns: repeat(4, 1fr); justify-items: center; gap: 14px; padding: 14px 18px; background: var(--sd-panel); border-radius: 10px; }
.sd-dm-svg { width: 92px; height: 92px; padding: 4px; color: #111; background: #fff; border-radius: 4px; }

.sd-decl { font-size: 11.5px; color: var(--sd-soft); text-align: center; }
.sd-mini th { font-size: 12px; padding: 7px 10px; }
.sd-mini td { font-size: 12px; padding: 9px 10px; overflow-wrap: anywhere; }

.sd-route { display: flex; background: #fff; border: 1.5px solid var(--sd-g); border-radius: 10px; overflow: hidden; }
.sd-route > div { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; padding: 8px 12px; border-left: 1px solid var(--sd-line); }
.sd-route > div:first-child { border-left: none; }
.sd-rt-top { font-size: 22px; font-weight: 800; line-height: 1.1; color: var(--sd-g); }
.sd-rt-chip { display: flex; align-items: center; gap: 6px; }
.sd-rt-chip i { font-style: normal; display: grid; place-items: center; min-width: 20px; height: 26px; padding: 0 4px; font-size: 15px; font-weight: 800; color: #fff; background: var(--sd-g); border-radius: 4px; }
.sd-rt-chip b { font-size: 22px; color: var(--sd-ink); }
.sd-lfoot { display: flex; justify-content: space-between; align-items: center; font-size: 12px; color: var(--sd-soft); }
.sd-lfoot b { font-size: 20px; letter-spacing: 1px; color: var(--sd-g); }

@media (max-width: 680px) {
  .sd-sheet { aspect-ratio: auto; }
  .sd-body { padding: 16px 12px 12px; gap: 12px; }
  .sd-head { grid-template-columns: 1fr; gap: 10px; }
  .sd-vline { display: none; }
  .sd-title { text-align: left; }
  .sd-logo { font-size: 42px; }
  .sd-meta { grid-template-columns: 1fr; gap: 10px; }
  .sd-meta-i { border-right: none; }
  .sd-addr { grid-template-columns: 1fr; }
  .sd-ship { grid-template-columns: 1fr 1fr; row-gap: 12px; }
  .sd-ship-i:nth-child(2) { border-right: none; }
  .sd-sum { grid-template-columns: 1fr; gap: 12px; }
  .sd-strip { grid-template-columns: 1fr 1fr; row-gap: 14px; }
  .sd-st-c:nth-child(2) { border-right: none; }
  .sd-perks { grid-template-columns: 1fr 1fr; row-gap: 14px; }
  .sd-perks > div:nth-child(2) { border-right: none; }
  .sd-bar { letter-spacing: .12em; padding: 12px; text-align: center; }
  .sd-awb { grid-template-columns: 1fr; }
  .sd-sort { grid-template-columns: 1fr; }
  .sd-dm { grid-template-columns: 1fr 1fr; row-gap: 12px; }
  .sd-route { flex-wrap: wrap; }
  .sd-route > div { flex: 1 1 45%; }
}
`